/**
 * ★ 全新完全自研组件化快速加购与多属性同步终极核心防御引擎
 * 针对原厂 Special_Double_Quote / Special_Slash 进行了双层清洗解码，100% 精准配对变体本地 JSON 数据矩阵
 */
// 🎯 动态计算并渲染促销价、原价与折扣百分比的全局纯函数 (100% 作用域安全)
const getPriceHtml = (variant) => {
  const price = variant.price;
  const comparePrice = variant.compare_at_price;
  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;
  const formattedPrice = formatPrice(price);
  
  if (comparePrice && comparePrice > price) {
    const formattedCompare = formatPrice(comparePrice);
    const discountPercent = Math.round((1 - price / comparePrice) * 100);
    return `
      <div class="qshop-price-container">
        <span class="qshop-price sale-price">${formattedPrice}</span>
        <span class="qshop-compare-price">${formattedCompare}</span>
        <span class="qshop-discount-badge">${discountPercent}% OFF</span>
      </div>
    `;
  }
  return `
    <div class="qshop-price-container">
      <span class="qshop-price">${formattedPrice}</span>
    </div>
  `;
};

// 🎯 动态获取当前语言包加购与售罄文案的辅助函数 (支持多语言自动适配)
const getAtcText = () => {
  if (window.variantStrings && window.variantStrings.addToCart) {
    return window.variantStrings.addToCart.toUpperCase();
  }
  return 'ADD TO BAG';
};

const getSoldOutText = () => {
  if (window.variantStrings && window.variantStrings.soldOut) {
    return window.variantStrings.soldOut.toUpperCase();
  }
  return 'SOLD OUT';
};

class CustomQuickShop extends HTMLElement {
  // 建立内存静态缓存表，防止重复进行深度 JSON 解析
  static productJsonCache = new Map();

  constructor() {
    super();
    this.container = this.querySelector('.qshop-container');
    this.overlay = this.querySelector('.qshop-overlay');
    this.closeBtn = this.querySelector('.qshop-close-btn');
    this.body = this.querySelector('.qshop-body');
    
    this.isPdpMode = this.getAttribute('data-is-pdp') === 'true';
    this.productData = null; // 当前正在运作商品的解密本地变体主数据集
    this.selectedOptions = []; // 缓存当前弹窗沙箱作用域内勾选的多属性数值链

    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

    // 🎯 键盘 ESC 键平滑退出弹窗
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });

    // 🎯 监听 Size Guide 点击，进入统一的弹窗控制与动态数据渲染流程
    this.addEventListener('click', (e) => {
      const sgBtn = e.target.closest('.qshop-size-guide-btn');
      if (sgBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.openSizeGuideModal();
      }
    });
    
    // 💻 PC 端缩略图滚轮平滑横向滚动支持 (提升桌面端纯鼠标滚轮交互体验)
    this.addEventListener('wheel', (e) => {
      if (window.innerWidth < 1025) return;
      const thumbs = e.target.closest('.qshop-pc-thumbs');
      if (thumbs && thumbs.scrollWidth > thumbs.clientWidth) {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
          thumbs.scrollLeft += e.deltaY;
        }
      }
    }, { passive: false });
    
    // 🎯 移动端阻断背景滚动的 touchmove 处理器
    this.preventBackgroundScroll = (e) => {
      const isScrollableArea = e.target.closest('.qshop-body, .qshop-media-scroller, .qshop-pc-thumbs');
      if (!isScrollableArea) {
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    };
  }

  /**
   * 运行时节点提权生命周期（彻底解决父级 transform 动画穿透引起的吸底隐形 Bug）
   */
  connectedCallback() {
    if (this.isPdpMode) {
      // 提权：强行剪切手写吸底栏至 body 最顶层根节点，保证其 fixed 绝对可见
      const stickyBar = document.getElementById('CustomHandcodedStickyBar');
      if (stickyBar && !stickyBar.hasAttribute('data-teleported')) {
        stickyBar.setAttribute('data-teleported', 'true');
        document.body.appendChild(stickyBar);
      }
      
      // 延迟 50 毫秒初始化，确保主页面原厂表单 DOM 选择器加载齐备，防止事件监听器漏空
      setTimeout(() => {
        this.initPdpStandaloneEngine();
      }, 50);
    }
    

    if (!this.hasAttribute('data-teleported')) {
      this.setAttribute('data-teleported', 'true');
      document.body.appendChild(this); // 将弹窗壳体也剪切提权到全站最高的 body 下
    }
  }

  /**
   * 对齐原厂：将原厂 DOM 内部特殊的转义字符（如 Special_Double_Quote）完美清洗解码为原始文本，彻底打破 Sold Out 锁死隐患
   */
  decodeOptionValue(val) {
    if (!val) return '';
    return val.split('Special_Double_Quote').join('"').split('Special_Slash').join('/');
  }

  /**
   * 对齐原厂：反向编码转义特殊符号
   */
  encodeOptionValue(val) {
    if (!val) return '';
    return val.split('"').join('Special_Double_Quote').split('/').join('Special_Slash');
  }

  renderErrorState() {
    if (!this.body) return;
    this.body.innerHTML = `
      <div style="padding: 48px 20px; text-align: center; color: #666;">
        <p style="font-size: 15px; margin-bottom: 16px;">Failed to load product details.</p>
        <button type="button" class="qshop-close-btn-error" style="padding: 10px 24px; background: #111; color: #fff; border: none; border-radius: 20px; font-size: 13px; font-weight: 600; cursor: pointer;">Close</button>
      </div>
    `;
    const btn = this.body.querySelector('.qshop-close-btn-error');
    if (btn) btn.addEventListener('click', () => this.close());
  }

  open(handle = null, preloadData = null, cartLine = null, currentVariantId = null, cartQuantity = null) {
    this.currentHandle = handle;
    this.cartLine = cartLine;
    this.currentVariantId = currentVariantId;
    this.cartQuantity = cartQuantity;

    // 清除未完成的退场定时器与退出状态
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
    const wasClosing = this.classList.contains('is-closing');
    this.classList.remove('is-closing');
    if (wasClosing) {
      this.classList.remove('is-open');
      void this.offsetWidth; // 触发回流重置关键帧动画
    }

    // 形态一：详情页 PDP 模式 (Mode A) - 保持 100% 独立，不碰任意 AJAX 网络 fetch 逻辑
    if (this.isPdpMode) {
      const pView = document.querySelector('.productView, [data-section-type="product"], .product-page-container');
      if (pView) {
        // ✅ 打开时立即同步当前选中的属性值与缩略图对齐
        this.refreshPdpPopupOptionsState(pView);
        this.updateStickyBarVariantInfo(pView);
      }
    } else if (preloadData) {
      // 形态二：商品列表卡片点击触发 (Mode B) - 外部已加载好数据直接渲染，不显示骨架屏
      this.productData = preloadData;
      this.userInteractedColor = false; // 🎯 进来默认展示封面主图，仅当用户主动点击颜色时才切换对应变体图
      this.lastSelectedColor = null;

      // 如果有传入当前 Variant ID，就自动高亮该 Variant 对应的规格选项
      let defaultVariant = null;
      if (currentVariantId) {
        defaultVariant = this.productData.variants.find(v => String(v.id) === String(currentVariantId));
        if (currentVariantId && defaultVariant) {
          this.userInteractedColor = true; // 若显式从购物车换款进入指定 Variant 则遵循该颜色
        }
      }
      if (!defaultVariant) {
        defaultVariant = this.productData.variants.find(v => v.available) || this.productData.variants[0];
      }
      this.selectedOptions = [...defaultVariant.options];

      // 🎯 在弹窗展开动画前装配完整内容，杜绝空壳抖动
      this.renderDynamicContent(window.innerWidth < 1025);
    }

    // ✅ 第1步：先锁定滚动位置（防止跳动）
    this.lockBodyScroll(true);
    
    // ✅ 第2步：然后再添加类，触发顺畅的关键帧动画
    this.classList.add('is-open');
  }


  close() {
    if (!this.classList.contains('is-open')) return;
    if (this.classList.contains('is-closing')) return;

    // 🎯 添加退场动画类，保留 is-open 维持 display: flex 250ms 直至平滑过渡完毕
    this.classList.add('is-closing');
    this.lockBodyScroll(false); // 释放滚动锁

    if (this._closeTimer) clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      this._closeTimer = null;
    }, 250);
  }

  openSizeGuideModal() {
    if (this.isPdpMode) {
      // PDP 模式：直接唤起页面上由原厂 Size Guide Section 渲染的静态弹窗
      const nativePopup = document.querySelector('.cider-size-modal');
      if (nativePopup) {
        nativePopup.style.zIndex = '2147483647';
        nativePopup.classList.add('is-active');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      }
      return;
    }

    // 商品列表 AJAX 模式：根据当前 productData 的 metafields 动态重绘并唤起弹窗
    if (!this.productData) return;

    let sizeModal = document.getElementById('cider-size-modal-global');
    if (!sizeModal) {
      // 若当前页面不存在尺码弹窗骨架（如 collection 页），则动态生成并挂载到 body 下
      sizeModal = document.createElement('div');
      sizeModal.id = 'cider-size-modal-global';
      sizeModal.className = 'cider-size-modal';
      sizeModal.setAttribute('aria-hidden', 'true');
      sizeModal.innerHTML = `
        <div class="cider-size-modal__overlay" data-modal-close> </div>
        <div class="cider-size-modal__content">
          <div class="cider-size-modal__header">
            <h3 class="cider-size-modal__title">SIZING HELP</h3>
            <button type="button" class="cider-size-modal__close" data-modal-close aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#111" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="cider-size-modal__body"></div>
        </div>
      `;
      document.body.appendChild(sizeModal);

      // 为动态生成的弹窗绑定关闭事件
      sizeModal.querySelectorAll('[data-modal-close]').forEach(closeBtn => {
        closeBtn.addEventListener('click', () => {
          sizeModal.classList.remove('is-active');
          document.documentElement.style.overflow = '';
          document.body.style.overflow = '';
        });
      });
    }

    // 动态渲染弹窗的主体内容
    const bodyEl = sizeModal.querySelector('.cider-size-modal__body');
    if (bodyEl) {
      this.renderSizeGuideBody(bodyEl);
    }

    // 展现尺码指南弹窗，置于最顶层
    sizeModal.style.zIndex = '2147483647';
    sizeModal.classList.add('is-active');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  renderSizeGuideBody(bodyEl) {
    let modelData = this.productData.metafields?.model_info;
    if (modelData) {
      if (typeof modelData === 'string') {
        try {
          modelData = JSON.parse(modelData);
        } catch (e) {
          modelData = null;
        }
      }
      if (Array.isArray(modelData)) {
        modelData = modelData[0];
      }
    }

    let html = '';
    
    // 1. 模特信息 (Model Information)
    if (modelData && Object.keys(modelData).length > 0) {
      const keys = Object.keys(modelData);
      const firstImg = this.productData.images?.[0] || '';
      const avatarHtml = firstImg 
        ? `<img src="${firstImg}" class="model-avatar" loading="lazy">`
        : `<span class="model-avatar-placeholder"></span>`;

      html += `
        <div class="cider-size-block">
          <h3 class="cider-size-block__heading">MODEL INFORMATION</h3>
          <div class="cider-size-table-wrapper">
            <table class="cider-size-table cider-model-table">
              <thead>
                <tr>
                  <th>Model</th>
                  ${keys.map(k => `<th>${k}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="cell-model-container">
                    <div class="cell-model">
                      <div class="model-avatar-wrapper">
                        ${avatarHtml}
                        <span class="model-size-badge">S</span>
                      </div>
                    </div>
                  </td>
                  ${keys.map(k => `<td>${modelData[k] || '-'}</td>`).join('')}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      `;
    }

    // 2. 版型 (Fit Type)
    const fitType = this.productData.metafields?.fit_type?.trim().toLowerCase();
    if (fitType) {
      let activeId = '';
      if (fitType.includes('slim')) activeId = 'slim';
      else if (fitType.includes('regular')) activeId = 'regular';
      else if (fitType.includes('oversized')) activeId = 'oversized';

      if (activeId) {
        html += `
          <div class="cider-size-block">
            <h3 class="cider-size-block__heading">Fit Type</h3>
            <div class="sg26-indicator" data-active="${activeId}">
              <div class="sg26-track">
                <div class="sg26-segment" data-id="slim"> </div>
                <div class="sg26-segment" data-id="regular"> </div>
                <div class="sg26-segment" data-id="oversized"> </div>
              </div>
              <div class="sg26-labels">
                <span data-id="slim">Slim</span>
                <span data-id="regular">Regular</span>
                <span data-id="oversized">Oversized</span>
              </div>
            </div>
          </div>
        `;
      }
    }

    // 3. 弹性 (Stretch)
    const stretch = this.productData.metafields?.stretch?.trim().toLowerCase();
    if (stretch) {
      let activeId = '';
      if (stretch.includes('non')) activeId = 'non';
      else if (stretch.includes('slight') || stretch.includes('sight')) activeId = 'slight';
      else if (stretch.includes('medium')) activeId = 'medium';
      else if (stretch.includes('high')) activeId = 'high';

      if (activeId) {
        html += `
          <div class="cider-size-block">
            <h3 class="cider-size-block__heading">Stretch</h3>
            <div class="sg26-indicator" data-active="${activeId}">
              <div class="sg26-track">
                <div class="sg26-segment" data-id="non"> </div>
                <div class="sg26-segment" data-id="slight"> </div>
                <div class="sg26-segment" data-id="medium"> </div>
                <div class="sg26-segment" data-id="high"> </div>
              </div>
              <div class="sg26-labels">
                <span data-id="non">Non</span>
                <span data-id="slight">Slight</span>
                <span data-id="medium">Medium</span>
                <span data-id="high">High</span>
              </div>
            </div>
          </div>
        `;
      }
    }

    // 4. 合身度 (How It Fits)
    const howItFits = this.productData.metafields?.how_it_fits?.trim().toLowerCase().replaceAll(' ', '-');
    if (howItFits) {
      let activeId = '';
      if (howItFits.includes('small')) activeId = 'small';
      else if (howItFits.includes('true') || howItFits.includes('ture')) activeId = 'true-to-size';
      else if (howItFits.includes('large')) activeId = 'large';

      if (activeId) {
        html += `
          <div class="cider-size-block">
            <h3 class="cider-size-block__heading">How It Fits</h3>
            <div class="sg26-indicator" data-active="${activeId}">
              <div class="sg26-track">
                <div class="sg26-segment" data-id="small"> </div>
                <div class="sg26-segment" data-id="true-to-size"> </div>
                <div class="sg26-segment" data-id="large"> </div>
              </div>
              <div class="sg26-labels">
                <span data-id="small">Small</span>
                <span data-id="true-to-size">True to size</span>
                <span data-id="large">Large</span>
              </div>
            </div>
          </div>
        `;
      }
    }

    // 5. 尺码对照表 / 尺码图片 (Measurements / Size Chart)
    let measurements = this.productData.metafields?.measurements;
    if (measurements && typeof measurements === 'string') {
      try {
        measurements = JSON.parse(measurements);
      } catch (e) {
        measurements = null;
      }
    }
    
    let sizeChartUrls = this.productData.metafields?.size_chart_urls;
    let sizeChartImages = [];
    if (sizeChartUrls && typeof sizeChartUrls === 'string' && sizeChartUrls.trim() !== '') {
      sizeChartImages = sizeChartUrls.split(',').map(url => url.trim()).filter(url => url !== '');
    }

    const hasMeasurements = measurements && ((measurements['in'] && measurements['in'].length > 0) || (measurements['cm'] && measurements['cm'].length > 0));
    const hasImages = sizeChartImages.length > 0;

    if (hasMeasurements || hasImages) {
      let defaultUnit = 'cm';
      if (hasMeasurements && measurements['in']?.length > 0 && (!measurements['cm'] || measurements['cm'].length === 0)) {
        defaultUnit = 'in';
      }
      
      const showToggle = (hasMeasurements && measurements['in']?.length > 0 && measurements['cm']?.length > 0) || (hasImages && sizeChartImages.length > 1);

      html += `
        <div class="cider-size-block cider-measurements-block">
          <div class="cider-measurements-header">
            <h3 class="cider-size-block__heading" style="margin: 0;">SIZE GUIDE</h3>
            ${showToggle ? `
              <div class="sg26-toggle-wrap" id="sg-dyn-unit-toggle">
                <span class="sg26-unit-label ${defaultUnit === 'cm' ? 'active' : ''}" data-u="cm">cm</span>
                <button type="button" class="sg26-switch-btn" id="sg-dyn-switch-btn" data-state="${defaultUnit}" aria-label="Toggle Unit">
                  <div class="sg26-switch-knob"> </div>
                </button>
                <span class="sg26-unit-label ${defaultUnit === 'in' ? 'active' : ''}" data-u="in">in</span>
              </div>
            ` : ''}
          </div>
          
          <div class="cider-size-table-wrapper" id="sg-dyn-table-container" style="display: ${hasMeasurements ? 'block' : 'none'};">
            <table class="cider-size-table measurements-table" id="cider-dyn-measurements-table">
              <thead></thead>
              <tbody></tbody>
            </table>
          </div>

          <div class="cider-size-image-wrapper" id="sg-dyn-image-container" style="display: ${!hasMeasurements && hasImages ? 'block' : 'none'};">
            <img src="" alt="Size Chart" class="cider-size-chart-img" id="sg-dyn-chart-img" loading="lazy">
          </div>
        </div>
      `;
    }

    // 免责声明
    html += `
      <div class="cider-size-disclaimer" style="margin-top:16px;padding-top:14px;border-top:1px solid #f0f0f0;">
        <p style="margin:0;font-size:12px;line-height:1.5;color:#999;">Note: Model fit for reference only. The size chart reflects body measurements, not product measurements. If you prefer a tighter or looser fit, consider sizing down or up.</p>
      </div>
    `;

    bodyEl.innerHTML = html;

    // 高亮各指示条中的活跃值
    bodyEl.querySelectorAll('.sg26-indicator').forEach(indicator => {
      const activeVal = indicator.getAttribute('data-active');
      if (activeVal) {
        const seg = indicator.querySelector(`.sg26-segment[data-id="${activeVal}"]`);
        if (seg) seg.style.backgroundColor = '#111111';
        const label = indicator.querySelector(`.sg26-labels span[data-id="${activeVal}"]`);
        if (label) {
          label.style.color = '#111111';
          label.style.fontWeight = '700';
        }
      }
    });

    // 绑定尺码表数据渲染与 cm/in 单位切换监听器
    if (hasMeasurements || hasImages) {
      let currentUnit = (hasMeasurements && measurements['in']?.length > 0 && (!measurements['cm'] || measurements['cm'].length === 0)) ? 'in' : 'cm';
      
      const toggleWrap = bodyEl.querySelector('#sg-dyn-unit-toggle');
      const switchBtn = bodyEl.querySelector('#sg-dyn-switch-btn');
      const tableContainer = bodyEl.querySelector('#sg-dyn-table-container');
      const imageContainer = bodyEl.querySelector('#sg-dyn-image-container');
      const chartImgEl = bodyEl.querySelector('#sg-dyn-chart-img');
      const tableThead = bodyEl.querySelector('#cider-dyn-measurements-table thead');
      const tableTbody = bodyEl.querySelector('#cider-dyn-measurements-table tbody');

      const renderTable = () => {
        if (!tableThead || !tableTbody) return;
        tableThead.innerHTML = ''; 
        tableTbody.innerHTML = '';
        
        const dataArray = measurements[currentUnit] || [];
        if (dataArray.length === 0) return;

        const headers = Object.keys(dataArray[0]);
        const trHead = document.createElement('tr');
        headers.forEach(headerText => {
          const th = document.createElement('th');
          th.textContent = headerText;
          trHead.appendChild(th);
        });
        tableThead.appendChild(trHead);

        dataArray.forEach(rowData => {
          const trBody = document.createElement('tr');
          headers.forEach(headerKey => {
            const td = document.createElement('td');
            td.textContent = rowData[headerKey] || '-';
            trBody.appendChild(td);
          });
          tableTbody.appendChild(trBody);
        });
      };

      const renderImage = () => {
        if (!chartImgEl) return;
        if (currentUnit === 'cm') {
          chartImgEl.src = sizeChartImages[0];
        } else {
          chartImgEl.src = sizeChartImages[1] ? sizeChartImages[1] : sizeChartImages[0];
        }
      };

      const updateUI = () => {
        if (toggleWrap) {
          toggleWrap.querySelectorAll('.sg26-unit-label').forEach(el => el.classList.remove('active'));
          const activeLabel = toggleWrap.querySelector(`.sg26-unit-label[data-u="${currentUnit}"]`);
          if (activeLabel) activeLabel.classList.add('active');
        }
        if (switchBtn) switchBtn.setAttribute('data-state', currentUnit);

        if (hasMeasurements) {
          if (tableContainer) tableContainer.style.display = 'block';
          if (imageContainer) imageContainer.style.display = 'none';
          renderTable();
        } else if (hasImages) {
          if (tableContainer) tableContainer.style.display = 'none';
          if (imageContainer) imageContainer.style.display = 'block';
          renderImage();
        }
      };

      if (toggleWrap) {
        toggleWrap.addEventListener('click', () => {
          currentUnit = currentUnit === 'in' ? 'cm' : 'in';
          updateUI();
        });
      }

      updateUI();
    }
  }

  /* ==========================================================================
     🛡️ 【形态一核心】详情页主商品移动端专属 · 零冲突物理双向闭环沙箱
     ========================================================================== */
  initPdpStandaloneEngine() {
    if (this.pdpEngineInitialized) return;
    this.pdpEngineInitialized = true;

    const pView = document.querySelector('.productView, [data-section-type="product"], .product-page-container');
    if (!pView) return;

    // 1. 读取详情页原厂在页面首屏注入的 application/json 原始变体配置数据集
    const masterJsonScript = pView.querySelector('variant-radios [type="application/json"], variant-selects [type="application/json"], [id*="VariantJson"], [data-product-json]');
    if (masterJsonScript) {
      try {
        this.productData = JSON.parse(masterJsonScript.textContent);
      } catch (err) {
        console.error('详情页主变体 JSON 配置矩阵读取失败', err);
      }
    }

    // 2. 绑定弹窗内点击属性 Swatch 事件
    this.addEventListener('click', (e) => {
      const swatchBtn = e.target.closest('.qswatch-btn');
      if (swatchBtn) {
        const row = swatchBtn.closest('.qshop-option-row');
        const optIndex = parseInt(row.getAttribute('data-option-index'));
        const optVal = swatchBtn.getAttribute('data-value');

        // 双向缓冲：点击时先更新内存 selectedOptions，防止从 DOM 异步回抽引发延迟或状态丢失
        if (!this.selectedOptions) this.selectedOptions = [];
        this.selectedOptions[optIndex] = optVal;

        // 🎯 优化：判定当前 Swatch 行的选项是否是 Color 颜色相关
        const optionLabel = row.querySelector('.qshop-option-label')?.textContent || '';
        const isColor = optionLabel.toLowerCase().includes('color') || optionLabel.toLowerCase().includes('colour');

        // 正向物理同步：驱动原厂 Form 表单（始终触发原厂 change 事件，确保主表单更新 Variant ID）
        this.syncToParentFormFields(pView, optIndex, optVal, true);
        this.refreshPdpPopupOptionsState(pView);
        this.updateStickyBarVariantInfo(pView);
      }
    });

    // 3. 逆向物理同步：外面改了属性，自动跟进同步弹窗内部高亮与吸底栏展示
    pView.addEventListener('change', () => {
      this.refreshPdpPopupOptionsState(pView);
      this.updateStickyBarVariantInfo(pView);
    });

    // 💻 PC 端缩略图点击切换主大图
    this.addEventListener('click', (e) => {
      const thumb = e.target.closest('.qshop-pc-thumb');
      if (thumb) {
        const mainImg = this.querySelector('[data-qshop-pdp-main-img], .qshop-pc-main-img');
        if (mainImg) {
          mainImg.src = thumb.getAttribute('data-src');
        }
        this.querySelectorAll('.qshop-pc-thumb').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      }
    });

    // 💻 初始化 PC 端吸底栏滚动穿过主加购按钮后显隐机制
    this.initPcStickyBarScroll();
    this.updateStickyBarVariantInfo(pView);

    // 4. 吸底大按钮点击的条件加购分流路由
    if (this.isPdpMode && !window.hasBoundStickyAtcClick) {
      window.hasBoundStickyAtcClick = true;
      document.addEventListener('click', (e) => {
        const stickyAtcTrigger = e.target.closest('#CustomStickyTriggerBtn');
        if (stickyAtcTrigger) {
          e.preventDefault();
          e.stopPropagation();

          // 💻 PC 端：统一直接平滑唤起 840px 居中快捷加购/选款弹窗（复用 custom-quick-shop）
          if (window.innerWidth >= 1025) {
            this.open();
            this.refreshPdpPopupOptionsState(pView);
            return;
          }

          // 📱 移动端：保持原先判定与加购分流
          const isSingleVariant = !this.productData || this.productData.length <= 1 || (this.productData[0] && (this.productData[0].title === 'Default Title' || this.productData[0].title.indexOf('Default') !== -1));
          if (isSingleVariant) {
            const defaultVariantId = (this.productData && this.productData[0]) ? this.productData[0].id : pView.querySelector('[name="id"]')?.value;
            if (defaultVariantId) {
              this.addToCartQuietly(defaultVariantId, 1, pView);
              return;
            }
          }

          const activeParentVariant = this.getPdpActiveVariantObject();
          
          // 直通车条件判定：检测当前属性是否已经全部选齐，并且属于可售状态
          if (activeParentVariant && activeParentVariant.available) {
            // 【路由 A】：已选齐 -> 静默加购，不打开购物车抽屉
            this.addToCartQuietly(activeParentVariant.id, 1, pView);
          } else {
            // 【路由 B】：漏选未齐 -> 顺利平滑拉开 Mode A 属性弹窗进行补选
            this.open();
            this.refreshPdpPopupOptionsState(pView);
          }
        }
      });
    }

    // 5. 弹窗内的 Add to Cart 大按钮代理真实物理表单加购
    const popupSubmits = this.querySelectorAll('.qshop-submit-btn');
    popupSubmits.forEach(popupSubmit => {
      popupSubmit.addEventListener('click', () => {
        const activeVariant = this.getPdpActiveVariantObject();
        if (activeVariant && activeVariant.available) {
          // 在关闭前，缓存当前按钮的 rect 坐标到内存中，供 runFlyToCartAnimation 自动定位起点
          this.lastClickRect = popupSubmit.getBoundingClientRect();
          this.addToCartQuietly(activeVariant.id, 1, pView);
          this.close();
        }
      });
    });
  }

  /**
   * 💻 PC 端吸底浮动栏滚动检测：主加购按钮离开屏幕视口时平滑滑出，回顶时收起
   */
  initPcStickyBarScroll() {
    const stickyBar = document.getElementById('CustomHandcodedStickyBar');
    if (!stickyBar) return;

    const findTargetBtn = () => {
      return document.getElementById('product-add-to-cart') ||
             document.querySelector('.productView-details [name="add"]') ||
             document.querySelector('.product-form__buttons') ||
             document.querySelector('.product-form__submit');
    };

    let targetBtn = findTargetBtn();

    const checkScroll = () => {
      if (window.innerWidth < 1025) {
        stickyBar.classList.remove('is-pc-visible');
        return;
      }
      if (!targetBtn) {
        targetBtn = findTargetBtn();
      }
      if (!targetBtn) return;
      const rect = targetBtn.getBoundingClientRect();
      if (rect.bottom < 0) {
        stickyBar.classList.add('is-pc-visible');
      } else {
        stickyBar.classList.remove('is-pc-visible');
      }
    };

    window.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll, { passive: true });
    checkScroll();
  }

  /**
   * 🔄 变体属性改变时，实时同步 PC 吸底栏价格、划线原价、缩略图与状态
   */
  updateStickyBarVariantInfo(pView) {
    const stickyBar = document.getElementById('CustomHandcodedStickyBar');
    if (!stickyBar) return;

    const activeVar = this.getPdpActiveVariantObject();
    if (!activeVar) return;

    const priceEl = stickyBar.querySelector('[data-pdp-sticky-price]');
    const compareEl = stickyBar.querySelector('[data-pdp-sticky-compare]');
    const thumbEl = stickyBar.querySelector('.sticky-bar-thumb');
    const atcBtn = stickyBar.querySelector('#CustomStickyTriggerBtn');

    if (priceEl) priceEl.textContent = `$${(activeVar.price / 100).toFixed(2)}`;
    if (compareEl) {
      if (activeVar.compare_at_price > activeVar.price) {
        compareEl.textContent = `$${(activeVar.compare_at_price / 100).toFixed(2)}`;
        compareEl.style.display = 'inline';
      } else {
        compareEl.style.display = 'none';
      }
    }
    if (activeVar.featured_image && thumbEl) {
      thumbEl.src = activeVar.featured_image.src;
    }
    if (atcBtn) {
      if (activeVar.available) {
        atcBtn.disabled = false;
        atcBtn.textContent = getAtcText();
      } else {
        atcBtn.disabled = true;
        atcBtn.textContent = getSoldOutText();
      }
    }

    // 联动同步弹窗内大图、缩略图高亮与价格，并自动平滑滚至当前颜色缩略图
    const modalMainImg = this.querySelector('[data-qshop-pdp-main-img]');
    if (modalMainImg && activeVar.featured_image) {
      const isImgChanged = !this.lastPdpActiveImgSrc || this.lastPdpActiveImgSrc !== activeVar.featured_image.src;
      this.lastPdpActiveImgSrc = activeVar.featured_image.src;

      modalMainImg.src = activeVar.featured_image.src;
      let matchedThumb = null;
      this.querySelectorAll('.qshop-pc-thumb').forEach(t => {
        const tSrc = t.getAttribute('data-src') || t.src;
        const cleanTSrc = tSrc ? tSrc.split('?')[0] : '';
        const cleanVarSrc = activeVar.featured_image.src ? activeVar.featured_image.src.split('?')[0] : '';
        const isMatch = cleanTSrc && cleanVarSrc && (cleanTSrc.includes(cleanVarSrc) || cleanVarSrc.includes(cleanTSrc));
        t.classList.toggle('is-active', isMatch);
        if (isMatch && !matchedThumb) {
          matchedThumb = t;
        }
      });
      // 🎯 核心交互：仅在颜色/主图真正发生改变时，将当前缩略图平滑自动居中滚动到视野内（切尺码不触发滚动）
      if (matchedThumb && isImgChanged) {
        setTimeout(() => {
          const thumbsContainer = matchedThumb.closest('.qshop-pc-thumbs');
          if (thumbsContainer) {
            const containerRect = thumbsContainer.getBoundingClientRect();
            const thumbRect = matchedThumb.getBoundingClientRect();
            const currentScroll = thumbsContainer.scrollLeft;
            const targetScroll = currentScroll + (thumbRect.left - containerRect.left) - (thumbsContainer.clientWidth / 2) + (matchedThumb.clientWidth / 2);
            thumbsContainer.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
          }
        }, 50);
      }
    }
    const modalPrice = this.querySelector('.qshop-pc-only-price .qshop-price');
    if (modalPrice) modalPrice.textContent = `$${(activeVar.price / 100).toFixed(2)}`;
  }

  getPdpActiveVariantObject() {
    if (!this.productData) return null;
    if (!this.selectedOptions || this.selectedOptions.length === 0) return null;

    // 检查所有选项是否都已选择（没有空值）
    const allSelected = this.selectedOptions.every(opt => opt && opt.trim() !== '');
    if (!allSelected) return null;

    return this.productData.find(v => v.options.every((o, i) => o === this.selectedOptions[i]));
  }

  syncToParentFormFields(pView, optIndex, value, triggerChange = true) {
    // 将我们的原始纯净文本（如 M）转换为原厂主题所期望 of 转义特征码（如 Special_Double_Quote），打破断裂缺陷
    const encodedVal = this.encodeOptionValue(value);
    
    // 🎯 优化：精准定位当前 Option Index 的输入控件，防止全局 select 污染
    const nativeInputs = pView.querySelectorAll(`
      fieldset[data-option-index="${optIndex}"] input, 
      [data-option-index="${optIndex}"] select,
      .product-form__input[data-option-index="${optIndex}"] input, 
      .product-form__input[data-option-index="${optIndex}"] select,
      [data-index="${optIndex}"] input,
      select#option-${optIndex}, 
      select[id*="Option-${optIndex}"]
    `);
    
    // 🎯 优化：分两阶段匹配。第一阶段：100% 精确的 === 全等匹配，杜绝 L 误中 XL、Red 误中 Wine Red
    let matchedAny = false;

    nativeInputs.forEach(input => {
      if (input.type === 'radio') {
        if (input.value === encodedVal || input.value === value) {
          input.checked = true;
          // 🎯 仅在 triggerChange 为 true 时，才去派发 change 事件驱动大图切换，彻底避开 input.click() 引起的非必要切换
          if (triggerChange) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          matchedAny = true;
        }
      } else if (input.tagName === 'SELECT') {
        Array.from(input.options).forEach(opt => {
          if (opt.value === encodedVal || opt.value === value || opt.text.trim() === value) {
            input.value = opt.value;
            if (triggerChange) {
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            matchedAny = true;
          }
        });
      }
    });

    // 只有在【完全找不到】任何全等匹配项时，才启动带分隔符的弱匹配，彻底杜绝包含子串（indexOf）导致的误匹配
    if (!matchedAny) {
      nativeInputs.forEach(input => {
        if (input.type === 'radio') {
          const splitVal = input.value.split(' - ')[0].trim();
          if (splitVal === encodedVal || splitVal === value) {
            input.checked = true;
            if (triggerChange) {
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        } else if (input.tagName === 'SELECT') {
          Array.from(input.options).forEach(opt => {
            const splitOptVal = opt.value.split(' - ')[0].trim();
            const splitOptText = opt.text.split(' - ')[0].trim();
            if (splitOptVal === encodedVal || splitOptVal === value || splitOptText === value) {
              input.value = opt.value;
              if (triggerChange) {
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          });
        }
      });
    }
  }

  refreshPdpPopupOptionsState(pView) {
    if (!this.productData) return;
    const rows = this.querySelectorAll('.qshop-option-row');
    if (!this.selectedOptions) this.selectedOptions = [];
    
    // 从外界主表单中，逆向回抽、清洗解码当前的变体多属性勾选数值
    rows.forEach((row, idx) => {
      const checkedField = pView.querySelector(`fieldset[data-option-index="${idx}"] input:checked, .product-form__input[data-option-index="${idx}"] input:checked`);
      let val = '';
      if (checkedField) {
        val = checkedField.value;
      } else {
        const selectField = pView.querySelector(`select#option-${idx}, select[id*="Option-${idx}"], .product-form__input[data-option-index="${idx}"] select`);
        if (selectField) val = selectField.value;
      }
      
      const decodedVal = this.decodeOptionValue(val);
      const optName = row.getAttribute('data-option-name') || '';
      const isSize = optName.toLowerCase().includes('size');

      // 🎯 优化：如果 DOM 回抽空值但内存中已经有数据，则不要覆盖内存数据，防止异步渲染导致的假“空属性”
      if (decodedVal) {
        // 如果是移动端且 URL 中没有指定 variant 参数，且这是第一次初始化弹窗状态，则强制清除尺码的选中状态
        const isMobile = window.innerWidth < 768;
        const hasExplicitVariant = window.location.search.includes('variant=');
        if (isMobile && !hasExplicitVariant && isSize && !this.pdpPopupOpened) {
          this.selectedOptions[idx] = '';
        } else {
          this.selectedOptions[idx] = decodedVal;
        }
      } else if (!this.selectedOptions[idx]) {
        if (isSize) {
          this.selectedOptions[idx] = ''; // 不自动默认选择尺码
        } else {
          // 兜底：如果都没有，则选取当前第一个可用/售罄变体的对应多属性作为初始填充
          const firstAvailable = this.productData.find(v => v.available) || this.productData[0];
          if (firstAvailable && firstAvailable.options[idx]) {
            this.selectedOptions[idx] = firstAvailable.options[idx];
          }
        }
      }

      row.querySelector('.qshop-current-val').innerText = this.selectedOptions[idx] || 'Select Size';
    });

    // 变体扫描并重绘售罄样式（.is-soldout）
    rows.forEach((row, idx) => {
      row.querySelectorAll('.qswatch-btn').forEach(btn => {
        const btnVal = btn.getAttribute('data-value');
        btn.classList.toggle('is-selected', btnVal === this.selectedOptions[idx]);

        // 运用原生的 .every() 交叉矩阵匹配算法自动算出结果，若有未选维度则忽略它的过滤限制，判断是否有任何可用变体
        const hasAvailableVariant = this.productData.some(v => {
          const matches = v.options.every((o, i) => {
            if (i === idx) {
              return o === btnVal;
            }
            if (!this.selectedOptions[i]) {
              return true; // 忽略未选维度
            }
            return o === this.selectedOptions[i];
          });
          return matches && v.available;
        });
        
        // ⭐ 售罄状态控制：允许点击（不加disabled属性），仅仅附加 .is-soldout 样式高亮斜线
        btn.classList.toggle('is-soldout', !hasAvailableVariant);
      });
    });

    // 置灰加购按钮：若当前选中的组合在 JSON 矩阵中属于完全售罄，置灰锁定 Add to Cart 大键
    const currentActiveVar = this.getPdpActiveVariantObject(pView);
    const submitBtn = this.querySelector('.qshop-submit-btn');
    if (submitBtn) {
      const hasUnselected = this.selectedOptions.some((opt, i) => !opt);
      if (hasUnselected) {
        submitBtn.disabled = true;
        submitBtn.innerText = (window.variantStrings && window.variantStrings.select) ? window.variantStrings.select.toUpperCase() : 'SELECT OPTIONS';
      } else if (currentActiveVar && currentActiveVar.available) {
        submitBtn.disabled = false;
        submitBtn.innerText = getAtcText();
      } else {
        submitBtn.disabled = true;
        submitBtn.innerText = getSoldOutText();
      }
    }

    // 延迟一丁点时间再标记 PDP 弹窗初始化完成，防止同一次打开过程中的多次同步调用导致清空失效
    setTimeout(() => {
      this.pdpPopupOpened = true;
    }, 100);
  }

  // --- PC / Card UI render section follows ---
  renderDynamicContent(isMobile) {
    const p = this.productData;
    const currentVariant = p.variants.find(v => 
      v.options.every((opt, i) => opt === this.selectedOptions[i])
    ) || p.variants[0];

    // 🎯 动态缓存滚动区的当前 scrollTop 位置，防止 innerHTML 覆盖导致滚动自动弹回最顶部
    const scrollContainer = this.body ? this.body.querySelector('.qshop-controls-wrapper') : null;
    const savedScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

    // 🎯 动态定位“颜色 (Color)”属性在当前商品中的 option 索引
    const colorOptIdx = p.options.findIndex(opt => opt.name.toLowerCase().includes('color') || opt.name.toLowerCase().includes('colour'));
    const currentColor = colorOptIdx !== -1 ? this.selectedOptions[colorOptIdx] : null;

    // 🎯 检查大图/缩略图媒体区域是否已经存在于 DOM 中
    const mediaSelector = isMobile ? '.qshop-media-scroller' : '.qshop-pc-left';
    const oldMediaDOM = this.body ? this.body.querySelector(mediaSelector) : null;
    const oldThumbsContainer = oldMediaDOM ? oldMediaDOM.querySelector('.qshop-pc-thumbs, .qshop-media-scroller') : null;
    const savedThumbsScrollLeft = oldThumbsContainer ? oldThumbsContainer.scrollLeft : 0;

    // 🎯 核心优化：若本次点击非颜色选项（颜色值未发生改变），则保留原有媒体 DOM 状态，防止大图闪烁与横滚归零
    const preserveMedia = oldMediaDOM && (currentColor === this.lastSelectedColor);
    
    // 暂存原有媒体 DOM 片段
    let savedMediaDOM = null;
    if (preserveMedia) {
      savedMediaDOM = oldMediaDOM;
    }
    
    // 更新上一轮的颜色值缓存
    this.lastSelectedColor = currentColor;

    const formatPrice = (cents) => `${(cents / 100).toFixed(2)}`;
    const getThumbUrl = (url, w = 300) => {
      if (!url || typeof url !== 'string') return url || '';
      if (url.includes('&width=') || url.includes('?width=')) {
        return url.replace(/([?&]width=)\d+/, `$1${w}`);
      }
      return url.includes('?') ? `${url}&width=${w}` : `${url}?width=${w}`;
    };

    // 🎯 核心图片选取：默认展示商品封面第1张主图；仅当用户主动点击切换颜色时，才切换为颜色变体图
    const productCoverImg = p.featured_image || (p.images && p.images[0]) || '';
    const variantImg = currentVariant.featured_image ? currentVariant.featured_image.src : productCoverImg;
    const mainImg = (this.userInteractedColor && variantImg) ? variantImg : productCoverImg;

    let baseHtml = '';

    if (isMobile) {
      // 📱 重绘还原移动端布局结构 (内含滚动区 controls-wrapper 和固定底部的 action-row)
      baseHtml = `
        <div class="qshop-controls-wrapper">
          <div class="qshop-mobile-header">
            <div class="qshop-media-scroller">
              ${p.images.map((img, idx) => {
                const isImgActive = (mainImg && (mainImg === img || mainImg.includes(img) || img.includes(mainImg))) || (!mainImg && idx === 0);
                return `
                  <div class="qshop-media-item ${isImgActive ? 'is-active' : ''}">
                    <img src="${getThumbUrl(img, 300)}" width="134.25" height="179" loading="lazy">
                  </div>
                `;
              }).join('')}
            </div>
            <div class="qshop-meta-box">
              <div class="qshop-title-row">
                <h3 class="qshop-title is-clamped">${p.title}</h3>
                <button type="button" class="qshop-title-toggle" style="display: none;">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </div>
            </div>
            <div class="qshop-info-split-row">
              ${getPriceHtml(currentVariant)}
              <a href="/products/${p.handle}" class="qshop-details-link-btn">SEE DETAILS &gt;</a>
            </div>
          </div>
          <!-- 动态插入属性 Swatches 的锚点 -->
          <div class="qshop-dynamic-options-anchor"></div>
        </div>
        <div class="qshop-action-row">
          <button type="button" class="qshop-submit-btn" ${!currentVariant.available ? 'disabled' : ''}>
            ${currentVariant.available ? (this.cartLine ? 'UPDATE TO BAG' : getAtcText()) : getSoldOutText()}
          </button>
        </div>
      `;
    } else {
      // 💻 重绘还原 PC 端布局结构 (右侧栏自带专属 qshop-pc-action-row 按钮，加置底 flex 布局)
      baseHtml = `
        <div class="qshop-controls-wrapper">
          <div class="qshop-pc-layout">
            <div class="qshop-pc-left">
              <img src="${mainImg}" class="qshop-pc-main-img" alt="${p.title}">
              <div class="qshop-pc-thumbs">
                ${p.images.map((img, idx) => {
                  const isActive = (mainImg && (mainImg === img || mainImg.includes(img) || img.includes(mainImg))) || (!mainImg && idx === 0);
                  return `<img src="${getThumbUrl(img, 150)}" class="qshop-pc-thumb ${isActive ? 'is-active' : ''}" data-src="${img}" alt="${p.title}">`;
                }).join('')}
              </div>
            </div>
            <div class="qshop-pc-right">
              <h3 class="qshop-title">${p.title}</h3>
              ${getPriceHtml(currentVariant)}
              <div class="qshop-dynamic-options-anchor"></div>
              
              <!-- 🎯 PC 端右侧栏专属加购按钮，通过 CSS margin-top: auto 牢牢钉死在右下角 -->
              <div class="qshop-pc-action-row">
                <button type="button" class="qshop-submit-btn" ${!currentVariant.available ? 'disabled' : ''}>
                  ${currentVariant.available ? (this.cartLine ? 'UPDATE TO BAG' : getAtcText()) : getSoldOutText()}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 移动端置底按钮容器 (在 PC 端会通过 CSS 被 display: none 隐藏) -->
        <div class="qshop-action-row">
          <button type="button" class="qshop-submit-btn" ${!currentVariant.available ? 'disabled' : ''}>
            ${currentVariant.available ? (this.cartLine ? 'UPDATE TO BAG' : getAtcText()) : getSoldOutText()}
          </button>
        </div>
      `;
    }

    this.body.innerHTML = baseHtml;

    // 🎯 核心优化：若判定为保留媒体 DOM，则把重绘后产生的占位 DOM 原地替换为我们暂存的旧 DOM，并保持滚动条位置绝对静止
    if (preserveMedia && savedMediaDOM) {
      const newMediaPlaceholder = this.body.querySelector(mediaSelector);
      if (newMediaPlaceholder && newMediaPlaceholder.parentNode) {
        newMediaPlaceholder.parentNode.replaceChild(savedMediaDOM, newMediaPlaceholder);
        const restoredThumbs = savedMediaDOM.querySelector('.qshop-pc-thumbs, .qshop-media-scroller');
        if (restoredThumbs && savedThumbsScrollLeft > 0) {
          restoredThumbs.scrollLeft = savedThumbsScrollLeft;
        }
      }
    }

    // 🎯 核心交互：PC 端仅在颜色/主图发生改变或初次渲染时（!preserveMedia），自动将当前选中的小图滚动到可视区域中央
    if (!isMobile && !preserveMedia) {
      setTimeout(() => {
        const matchedThumb = this.body.querySelector('.qshop-pc-thumb.is-active');
        if (matchedThumb) {
          const thumbsContainer = matchedThumb.closest('.qshop-pc-thumbs');
          if (thumbsContainer) {
            const containerRect = thumbsContainer.getBoundingClientRect();
            const thumbRect = matchedThumb.getBoundingClientRect();
            const currentScroll = thumbsContainer.scrollLeft;
            const targetScroll = currentScroll + (thumbRect.left - containerRect.left) - (thumbsContainer.clientWidth / 2) + (matchedThumb.clientWidth / 2);
            thumbsContainer.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
          }
        }
      }, 50);
    }

    // 2. 动态拼装并塞入属性选择器 Swatches
    const anchor = this.body.querySelector('.qshop-dynamic-options-anchor');
    if (anchor) {
      p.options.forEach((opt, idx) => {
        const row = document.createElement('div');
        row.className = 'qshop-option-row';
        
        const optNameDown = opt.name.toLowerCase();
        const isSizeOption = optNameDown.includes('size');

        row.innerHTML = `
          <div class="qshop-option-header">
            <span class="qshop-option-label">${opt.name}: <strong class="qshop-current-val">${this.selectedOptions[idx]}</strong></span>
            ${isSizeOption ? `<span class="qshop-size-guide-btn" data-modal-trigger="cider-size-modal-global">Size Guide &gt;</span>` : ''}
          </div>
          <div class="qshop-swatches">
            ${opt.values.map(val => {
              const isSelected = this.selectedOptions[idx] === val;
              
              const isSoldout = !p.variants.some(v => {
                const matches = v.options.every((o, i) => {
                  if (i === idx) {
                    return o === val;
                  }
                  if (!this.selectedOptions[i]) {
                    return true;
                  }
                  return o === this.selectedOptions[i];
                });
                return matches && v.available;
              });

              return `<button type="button" class="qswatch-btn ${isSelected ? 'is-selected' : ''} ${isSoldout ? 'is-soldout' : ''}" data-index="${idx}" data-value="${encodeURIComponent(val)}">${val}</button>`;
            }).join('')}
          </div>
        `;
        anchor.appendChild(row);
      });
    }

    // 3. 🎯 标题长行截断与点击展开/收起核心计算逻辑 (纯 JavaScript 动态检测溢出)
    const titleEl = this.body.querySelector('.qshop-title');
    const toggleBtn = this.body.querySelector('.qshop-title-toggle');
    if (titleEl && toggleBtn) {
      titleEl.classList.add('is-clamped');
      const isOverflowing = titleEl.scrollWidth > titleEl.clientWidth;
      if (isOverflowing) {
        toggleBtn.style.display = 'inline-flex';
        
        const titleRow = this.body.querySelector('.qshop-title-row');
        if (titleRow) {
          titleRow.style.cursor = 'pointer';
          titleRow.addEventListener('click', () => {
            const clamped = titleEl.classList.toggle('is-clamped');
            toggleBtn.classList.toggle('is-expanded', !clamped);
          });
        }
      } else {
        toggleBtn.style.display = 'none';
      }
    }

    this.bindSandboxCardEvents(isMobile, currentVariant.id);
    
    // 🎯 终极滚动条高度同步锁定复位，放在函数最后，保证所有动态 DOM 撑高后完美复位
    if (scrollContainer && savedScrollTop > 0) {
      const newScrollContainer = this.body.querySelector('.qshop-controls-wrapper');
      if (newScrollContainer) {
        newScrollContainer.scrollTop = savedScrollTop;
      }
    }
  }

  bindSandboxCardEvents(isMobile, variantId) {
    const p = this.productData;
    this.body.querySelectorAll('.qswatch-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.getAttribute('data-index'));
        const val = decodeURIComponent(btn.getAttribute('data-value'));

        // 🎯 核心：检测用户是否点击了颜色 (Color) 属性
        const optName = p && p.options && p.options[idx] && p.options[idx].name ? p.options[idx].name.toLowerCase() : '';
        if (optName.includes('color') || optName.includes('colour')) {
          this.userInteractedColor = true;
        }

        this.selectedOptions[idx] = val;
        this.renderDynamicContent(isMobile);
      });
    });

    if (!isMobile) {
      this.body.querySelectorAll('.qshop-pc-thumb').forEach(thumb => {
        thumb.addEventListener('click', () => {
          this.body.querySelector('.qshop-pc-main-img').src = thumb.getAttribute('data-src');
          this.body.querySelectorAll('.qshop-pc-thumb').forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    }

    const mToggle = this.body.querySelector('.qshop-title-toggle');
    if (mToggle) {
      mToggle.addEventListener('click', () => {
        const titleEl = this.body.querySelector('.qshop-title');
        const isClamped = titleEl.classList.toggle('is-clamped');
        mToggle.innerText = isClamped ? mToggle.getAttribute('data-expand-text') : mToggle.getAttribute('data-collapse-text');
      });
    }

    this.body.querySelector('.qshop-submit-btn').addEventListener('click', async (e) => {
      const originalText = e.target.innerText || getAtcText();
      
      if (this.cartLine) {
        // 🎯 购物车换绑变体逻辑
        e.target.disabled = true;
        e.target.innerText = 'Updating...';
        
        try {
          // 1. 移出购物车当前项
          const removeResponse = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: `quantity=0&id=${this.cartLine}`
          });
          
          if (removeResponse.ok) {
            // 2. 加购新选定的 Variant
            const addResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: this.cartQuantity || 1 })
            });
            
            if (addResponse.ok) {
              // 🎯 局部静默更新机制：弹窗保持显示 Updating...，待后台真实数据装配就绪后才优雅关窗
              try {
                const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
                if (cartRes.ok) {
                  const cartData = await cartRes.json();

                  // 1. 普通模式：调用主题 Section Rendering 静默替换商品行与合计金额
                  if (window.halo && typeof window.halo.updateCart === 'function' && !window.isCreatorCartMode) {
                    window.halo.updateCart(cartData);
                  }

                  // 2. 全局广播 cart:updated 事件（使用专属 source 避免触发 main-cart.liquid 的二次重复渲染）
                  document.dispatchEvent(new CustomEvent('cart:updated', {
                    bubbles: true,
                    detail: {
                      cart: cartData,
                      total_price: cartData.total_price,
                      item_count: cartData.item_count,
                      source: 'cart-variant-inline'
                    }
                  }));

                  // 3. 实时同步 PC 顶栏右上角购物袋角标数量
                  this.updateCartCount();

                  // 4. 背景真实数据全部完成静默替换后，平滑收起弹窗
                  this.close();
                } else {
                  window.location.reload(); // 偶发网络异常时的安全降级兜底
                }
              } catch (_err) {
                window.location.reload(); // 异常降级兜底
              }
            } else {
              const errData = await addResponse.json();
              if (errData && errData.description) {
                alert(errData.description);
              } else {
                alert('Failed to update variant. Please check stock.');
              }
              if (window.hideFullScreenLoading) window.hideFullScreenLoading();
            }
          } else {
            if (window.hideFullScreenLoading) window.hideFullScreenLoading();
          }
        } catch (err) {
          console.error('Update cart variant failed:', err);
          if (window.hideFullScreenLoading) window.hideFullScreenLoading();
        } finally {
          e.target.disabled = false;
          e.target.innerText = originalText;
        }
        return;
      }
      
      e.target.disabled = true;
      e.target.innerText = 'Adding...';
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        
        if (response.ok) {
          this.close();
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'quick-shop' } }));
          
          this.runFlyToCartAnimation(e.target, () => {
            this.updateCartCount();
          });

          if (window.Shopify && window.halo && typeof window.halo.updateSidebarCart === 'function') {
            window.Shopify.getCart((cart) => {
              window.halo.updateSidebarCart(cart);
            });
          }
          const isCartPage = document.body.classList.contains('template-cart') || window.location.pathname.startsWith('/cart');
          if (window.innerWidth >= 1025 && !isCartPage) {
            document.body.classList.add('cart-sidebar-show');
          }
        }
      } catch (err) {
        console.error('AJAX ATC 快速加购流进程异常:', err);
      } finally {
        e.target.disabled = false;
        e.target.innerText = originalText;
      }
    });
  }

  /**
   * ⭐ 静默加购方法：使用 Shopify AJAX API 添加商品到购物车，不触发任何跳转或抽屉
   */
  async addToCartQuietly(variantId, quantity = 1, pView = null) {
    let stickyBtn = null;
    let popupBtn = null;
    let originalText = '';

    try {
      stickyBtn = document.getElementById('CustomStickyTriggerBtn');
      popupBtn = this.querySelector('.qshop-submit-btn');
      const activeBtn = (popupBtn && popupBtn.disabled === false) ? popupBtn : (stickyBtn || popupBtn);

      // 显示加载状态
      originalText = stickyBtn ? stickyBtn.textContent : '';

      if (stickyBtn) {
        stickyBtn.disabled = true;
        stickyBtn.textContent = 'Adding...';
      }
      if (popupBtn) {
        popupBtn.disabled = true;
        popupBtn.textContent = 'Adding...';
      }

      // 调用 Shopify AJAX API 加购
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Add to cart failed');
      }

      const result = await response.json();

      // 触发购物车更新事件（供其他组件监听）
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { item: result, variantId, quantity, source: 'quick-shop' }
      }));

      // 🎯 只有在加购成功后，才触发抛物线飞入与数量气泡更新
      this.runFlyToCartAnimation(activeBtn, () => {
        this.updateCartCount();
      });

      // 静默加购成功后，虽然不打开抽屉，但也需要更新侧边栏购物车的数据，保证数据同步
      if (window.Shopify && window.halo && typeof window.halo.updateSidebarCart === 'function') {
        window.Shopify.getCart((cart) => {
          window.halo.updateSidebarCart(cart);
        });
      }

      // 恢复按钮状态
      if (stickyBtn) {
        stickyBtn.textContent = originalText;
        stickyBtn.disabled = false;
      }

      if (popupBtn) {
        popupBtn.textContent = getAtcText();
        popupBtn.disabled = false;
      }

      return result;
    } catch (error) {
      console.error('Add to cart error:', error);

      // 恢复按钮状态
      if (stickyBtn) {
        stickyBtn.textContent = originalText;
        stickyBtn.disabled = false;
      }

      if (popupBtn) {
        popupBtn.textContent = getAtcText();
        popupBtn.disabled = false;
      }

      return null;
    }
  }

  /**
   * ⭐ 更新购物车数量显示：获取最新购物车数据并更新所有购物车数量元素
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');

      const cart = await response.json();

      // 查找所有可能的购物车数量显示元素
      const cartCountSelectors = [
        '.cart-count',
        '.cart-item-count',
        '[data-cart-count]',
        '.header__cart-count',
        '.cart-icon__count',
        '.cart-count-bubble',
        '#CartCount',
        '.cart-toggle__count'
      ];

      const cartCountElements = document.querySelectorAll(cartCountSelectors.join(', '));

      if (cartCountElements.length === 0) {
        console.warn('Cart count elements not found. Please check selectors.');
      }

      cartCountElements.forEach(el => {
        const oldCount = parseInt(el.textContent) || 0;
        const newCount = cart.item_count;

        el.textContent = newCount;

        // 添加动画效果
        el.classList.add('cart-count-updated');

        // 如果数量变化，添加额外的高亮效果
        if (oldCount !== newCount) {
          el.classList.add('cart-count-changed');
          setTimeout(() => {
            el.classList.remove('cart-count-changed');
          }, 500);
        }

        setTimeout(() => {
          el.classList.remove('cart-count-updated');
        }, 300);
      });

      // 也更新 data 属性，方便其他 JS 使用
      document.body.setAttribute('data-cart-item-count', cart.item_count);

      return cart;
    } catch (error) {
      console.error('Update cart count error:', error);
      return null;
    }
  }

    /**
     * 纯正移动阻尼级防背景滚动穿透锁
     */
  /**
   * 🚀 移动端商品卡片主图飞入顶部购物车抛物线过渡动效
   */
  runFlyToCartAnimation(activeBtn, onComplete) {
    if (window.innerWidth >= 1025) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    // 1. 寻找起点坐标（当前点击的加购/吸底按钮）
    let startRect = null;
    if (activeBtn && typeof activeBtn.getBoundingClientRect === 'function') {
      const rect = activeBtn.getBoundingClientRect();
      if (rect.width > 0) {
        startRect = rect;
      }
    }
    
    // 如果没有找到，或者按钮不可见，优先使用我们之前缓存的 lastClickRect，否则自动去寻找页面上的可见加购按钮
    if ((!startRect || startRect.width === 0) && this.lastClickRect) {
      startRect = this.lastClickRect;
    }
    
    if (!startRect || startRect.width === 0) {
      const fallbackBtn = document.querySelector('.custom-qshop-modal.is-open .qshop-submit-btn, #CustomStickyTriggerBtn, .product-form__submit, .custom-sticky-atc-btn');
      if (fallbackBtn) {
        startRect = fallbackBtn.getBoundingClientRect();
      }
    }

    // 如果连起跑线都没有，直接退出
    if (!startRect || startRect.width === 0) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    // 2. 寻找终点：右上角购物车图标
    const cartIcon = document.querySelector('.cart-icon-bubble, #cart-icon-bubble, .header__icon--cart, .cart-toggle, [href*="/cart"]');
    let endRect = null;
    if (cartIcon) {
      const rect = cartIcon.getBoundingClientRect();
      if (rect.left > 0 || rect.top > 0) {
        endRect = rect;
      }
    }
    if (!endRect) {
      endRect = {
        left: window.innerWidth - 60,
        top: 20,
        width: 30,
        height: 30
      };
    }

    // 3. 寻找商品可见大图作为飞行体缩略图的图案源
    let sourceImg = null;
    const candidates = [
      '.custom-qshop-modal.is-open .qshop-pc-main-img',
      '.custom-qshop-modal.is-open .qshop-media-item.is-active img',
      '.custom-qshop-modal.is-open img',
      '.productView-image-carousel .slick-active img',
      '.productView-image-carousel .swiper-slide-active img',
      '.productView-image',
      '.productView-images img',
      '[data-image-gallery] img',
      '.product__media img',
      'img[src*="/products/"]'
    ];

    for (let selector of candidates) {
      const imgs = document.querySelectorAll(selector);
      for (let img of imgs) {
        const rect = img.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0 && img.src && !img.src.includes('data:image')) {
          sourceImg = img;
          break;
        }
      }
      if (sourceImg) break;
    }

    // 清洗提取图片链接，防范 lazyload base64 占位图
    let imgSrc = '';
    if (sourceImg) {
      imgSrc = sourceImg.getAttribute('data-src') || sourceImg.getAttribute('data-lazy-src') || sourceImg.src;
    } else {
      const cardImg = document.querySelector('.product-item img, .product-card img, .grid-view-item__image');
      imgSrc = cardImg ? (cardImg.getAttribute('data-src') || cardImg.src) : '';
    }

    // 4. 创建高阶抛物线飞行包裹容器与缩略图本身
    const flyerWrapper = document.createElement('div');
    const flyer = document.createElement('img');
    flyer.src = imgSrc || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100%" height="100%" fill="%23eeeeee"/></svg>';

    // 父容器负责 X 轴横向 GPU 加速移动
    flyerWrapper.style.position = 'fixed';
    flyerWrapper.style.left = `${startRect.left + startRect.width / 2 - 60}px`;
    flyerWrapper.style.top = `${startRect.top + startRect.height / 2 - 60}px`;
    flyerWrapper.style.width = '120px';
    flyerWrapper.style.height = '120px';
    flyerWrapper.style.zIndex = '2147483647';
    flyerWrapper.style.pointerEvents = 'none';
    flyerWrapper.style.willChange = 'transform';
    flyerWrapper.style.transition = 'transform 0.85s linear';

    // 子图片负责 Y 轴贝塞尔高抛曲线与缩小
    flyer.style.width = '100%';
    flyer.style.height = '100%';
    flyer.style.borderRadius = '4px';
    flyer.style.objectFit = 'cover';
    flyer.style.boxShadow = '0 6px 15px rgba(0,0,0,0.18)';
    flyer.style.border = '1.5px solid rgba(255,255,255,0.9)';
    flyer.style.willChange = 'transform, opacity';
    flyer.style.transition = 'transform 0.85s cubic-bezier(0.1, 0.8, 0.3, 1.15), opacity 0.85s ease';

    flyerWrapper.appendChild(flyer);
    document.body.appendChild(flyerWrapper);

    const diffX = (endRect.left + endRect.width / 2) - (startRect.left + startRect.width / 2);
    const diffY = (endRect.top + endRect.height / 2) - (startRect.top + startRect.height / 2);

    window.getComputedStyle(flyerWrapper).transform;

    // 5. 驱动 GPU 加速动画开始
    requestAnimationFrame(() => {
      flyerWrapper.style.transform = `translate3d(${diffX}px, 0, 0)`;
      flyer.style.transform = `translate3d(0, ${diffY}px, 0) scale(0.20)`;
      flyer.style.opacity = '0.35';
    });

    // 6. 动画结束：触发数字更新、爆破特效及弹性反馈
    flyer.addEventListener('transitionend', () => {
      flyerWrapper.remove();
      
      if (typeof onComplete === 'function') onComplete();
      
      // 购物车涟漪效果
      const ripple = document.createElement('div');
      ripple.style.position = 'fixed';
      ripple.style.left = `${endRect.left + endRect.width / 2 - 20}px`;
      ripple.style.top = `${endRect.top + endRect.height / 2 - 20}px`;
      ripple.style.width = '40px';
      ripple.style.height = '40px';
      ripple.style.borderRadius = '50%';
      ripple.style.border = '2px solid #000000';
      ripple.style.zIndex = '2147483646';
      ripple.style.pointerEvents = 'none';
      ripple.style.transition = 'all 0.4s ease-out';
      ripple.style.transform = 'scale(0.2)';
      ripple.style.opacity = '1';
      document.body.appendChild(ripple);
      
      requestAnimationFrame(() => {
        ripple.style.transform = 'scale(1.6)';
        ripple.style.opacity = '0';
      });
      setTimeout(() => ripple.remove(), 400);

      if (cartIcon) {
        cartIcon.classList.add('cart-icon-pop-bounce');
        setTimeout(() => {
          cartIcon.classList.remove('cart-icon-pop-bounce');
        }, 550);
      }
    });
  }

  lockBodyScroll(lock) {
    if (lock) {
      // 🎯 100% 对齐原厂 Cider 尺寸表锁定逻辑，双重锁死 html 和 body 滚动条，绝对防穿透
      this.oldHtmlOverflow = document.documentElement.style.overflow;
      this.oldBodyOverflow = document.body.style.overflow;
      
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      // 🎯 还原滚动状态
      document.documentElement.style.overflow = this.oldHtmlOverflow || '';
      document.body.style.overflow = this.oldBodyOverflow || '';
    }
  }
}
if (!customElements.get('custom-quick-shop')) {
  customElements.define('custom-quick-shop', CustomQuickShop);
}

/**
 * 🛡️ 辅助函数：双通道防弹级异步数据拉取引擎
 * 自动拦截手势取消/连击 AbortError，无感降级请求官方原生 API，彻底摒弃阻塞式 alert 弹窗
 */
/**
 * 🛡️ 辅助函数：双通道防弹级异步数据拉取引擎 (内置内存高速缓存)
 * 自动拦截手势取消/连击 AbortError，无感降级请求官方原生 API，彻底摒弃阻塞式 alert 弹窗
 */
const loadQshopProductDataSafely = async (handle) => {
  if (CustomQuickShop.productJsonCache && CustomQuickShop.productJsonCache.has(handle)) {
    return CustomQuickShop.productJsonCache.get(handle);
  }

  try {
    const res = await fetch(`/products/${handle}?view=quick-shop-ajax`);
    if (res.ok) {
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const jsonEl = doc.getElementById('QshopProductJson');
      if (jsonEl && jsonEl.textContent) {
        const data = JSON.parse(jsonEl.textContent);
        if (CustomQuickShop.productJsonCache) CustomQuickShop.productJsonCache.set(handle, data);
        return data;
      }
    }
  } catch (e) {
    if (e.name === 'AbortError') return null;
    console.warn('Custom AJAX view load failed or interrupted, falling back to official API:', e);
  }

  // 🎯 自动静默降级：拉取 Shopify 官方原生 Product JSON API
  try {
    const jsRes = await fetch(`/products/${handle}.js`);
    if (jsRes.ok) {
      const data = await jsRes.json();
      if (CustomQuickShop.productJsonCache) CustomQuickShop.productJsonCache.set(handle, data);
      return data;
    }
  } catch (e) {
    if (e.name === 'AbortError') return null;
    console.error('Shopify official API fetch returned error:', e);
  }
  return null;
};

/**
 * 🚀 全局极速性能事件委托：唯一捕获全站商品卡片右上角图标点击（全面兼容04与04x）
 */
document.addEventListener('click', (e) => {
  // 🎯 监听 Size Guide 点击，自动提权 cider-size-modal 弹窗层级，使其叠盖在我们的二十亿层级弹窗之上
  const sgBtn = e.target.closest('[data-modal-trigger="cider-size-modal-global"]');
  if (sgBtn && sgBtn.classList.contains('qshop-size-guide-btn')) {
    const nativePopup = document.querySelector('.cider-size-modal');
    if (nativePopup) {
      nativePopup.style.zIndex = '2147483647';
    }
  }

  const cardTrigger = e.target.closest('.custom-card-qshop-trigger');
  if (cardTrigger) {
    e.preventDefault();
    e.stopPropagation();

    // 如果按钮正在加载中，禁止二次重复点击
    if (cardTrigger.classList.contains('is-loading')) return;

    const productItemNode = cardTrigger.closest('.product-item, .product-card, .grid-item, [data-product-handle]');
    if (!productItemNode) return;

    const productHandle = productItemNode.getAttribute('data-product-handle');
    if (!productHandle) return;

    const globalModalSingleton = document.getElementById('GlobalQuickShop');
    if (!globalModalSingleton) return;

    // 🎯 1. 命中内存缓存：0ms 瞬间秒开完整内容
    if (CustomQuickShop.productJsonCache && CustomQuickShop.productJsonCache.has(productHandle)) {
      const cachedData = CustomQuickShop.productJsonCache.get(productHandle);
      globalModalSingleton.open(productHandle, cachedData);
      return;
    }

    // 🎯 2. 无缓存时：按钮显示加载中状态，后台静默拉取数据
    cardTrigger.classList.add('is-loading');

    // 🎯 3. 后台安全拉取，数据加载完毕后再弹出完整弹窗（绝无白屏/骨架跳动）
    loadQshopProductDataSafely(productHandle)
      .then(ajaxData => {
        cardTrigger.classList.remove('is-loading');
        if (globalModalSingleton && ajaxData) {
          globalModalSingleton.open(productHandle, ajaxData);
        }
      })
      .catch(err => {
        cardTrigger.classList.remove('is-loading');
        console.error('AJAX Load Error:', err);
      });
  }
});

/**
 * 🎯 移动端详情页尺码框弹窗触发器
 */
window.openMobileCartDrawer = function(productHandle) {
  const pdpModal = document.querySelector('.custom-qshop-modal.m-pdp-sync');
  const pView = document.querySelector('.productView, [data-section-type="product"], .product-page-container');
  
  if (pdpModal) {
    pdpModal.open();
    // ✅ 立即同步当前选中的颜色/尺码值
    if (pView) {
      pdpModal.refreshPdpPopupOptionsState(pView);
    }
  }
};

// 🎯 仅在移动端劫持右上角购物车按钮点击，强制跳转到 /cart，PC端放行保留抽屉 (方案 B)
document.addEventListener('click', (e) => {
  const cartLink = e.target.closest('#cart-icon-bubble, .header__icon--cart, [href="/cart"]');
  if (cartLink) {
    const isMobile = window.innerWidth < 1025;
    if (isMobile) {
      // 1. 强行阻断所有原厂/第三方 App 脚本的“拉开抽屉”点击拦截
      e.preventDefault();
      e.stopPropagation();
      // 2. 强行跳转到购物车结算页面
      window.location.href = '/cart';
    }
  }
}, true); // 🎯 开启捕获阶段优先执行，确保在原厂和 App 脚本运行之前抢先截断！

/**
 * 🛒 购物车页面 — 尺码颜色药丸按钮点击 → 弹出 GlobalQuickShop 选择器
 */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.custom-cart-variant-trigger');
  if (!trigger) return;

  e.preventDefault();
  e.stopPropagation();

  const productHandle = trigger.getAttribute('data-product-handle');
  if (!productHandle) return;

  const globalModal = document.getElementById('GlobalQuickShop');
  if (!globalModal) return;

  // 防止重复点击
  if (trigger.classList.contains('is-loading')) return;

  const cartLine = trigger.getAttribute('data-line');
  const variantId = trigger.getAttribute('data-variant-id');
  const quantity = parseInt(trigger.getAttribute('data-quantity')) || 1;

  // 🎯 1. 命中内存缓存：0ms 瞬间秒开
  if (CustomQuickShop.productJsonCache && CustomQuickShop.productJsonCache.has(productHandle)) {
    const cachedData = CustomQuickShop.productJsonCache.get(productHandle);
    globalModal.open(productHandle, cachedData, cartLine, variantId, quantity);
    return;
  }

  // 🎯 2. 无缓存：按钮显示加载中转圈，后台拉取数据就绪后再弹出弹窗
  trigger.classList.add('is-loading');

  // 🎯 3. 后台安全拉取，数据加载完毕后再弹出完整弹窗（绝无白屏/骨架跳动）
  loadQshopProductDataSafely(productHandle)
    .then(ajaxData => {
      trigger.classList.remove('is-loading');
      if (globalModal && ajaxData) {
        globalModal.open(productHandle, ajaxData, cartLine, variantId, quantity);
      }
    })
    .catch(err => {
      trigger.classList.remove('is-loading');
      console.error('Cart variant quick shop error:', err);
    });
});

/**
 * 📊 Sizing Guide GA4 Analytics Tracking Engine
 */
(function() {
  // 辅助函数：识别当前页面类型
  function getPageType() {
    const path = window.location.pathname;
    if (path === '/' || path === '') return 'home';
    if (path.startsWith('/products/')) return 'product';
    if (path.startsWith('/collections/')) return 'collection';
    if (path.startsWith('/cart')) return 'cart';
    return 'other';
  }

  // 内存全局状态，用来记录当前打开弹窗时的元数据及时间戳
  window.sizeGuideTrackerState = {
    openTime: null,
    productId: '',
    productTitle: '',
    sourcePosition: ''
  };

  document.addEventListener('click', (e) => {
    // ==================== 1. 监测点击 & 展现事件 ====================
    const trigger = e.target.closest('[data-modal-trigger="cider-size-modal-global"]');
    if (trigger) {
      // 自动判定触发入口位置
      let sourcePosition = 'unknown';
      let productId = '';
      let productTitle = '';

      if (trigger.closest('custom-quick-shop')) {
        const qshop = trigger.closest('custom-quick-shop');
        const isPdp = qshop.getAttribute('data-is-pdp') === 'true';
        sourcePosition = isPdp ? 'pdp_sticky_quick_shop' : 'list_card_quick_shop';
        
        if (qshop.productData) {
          productId = qshop.productData.id || '';
          productTitle = qshop.productData.title || '';
        }
      } else if (trigger.classList.contains('mobile-size-guide-btn')) {
        sourcePosition = 'pdp_mobile_drawer';
      } else if (trigger.classList.contains('drawer-size-guide-trigger') || trigger.closest('.drawer-size-guide-trigger')) {
        sourcePosition = 'pdp_size_selector';
      } else {
        sourcePosition = 'pdp_other_trigger';
      }

      // 详情页兜底数据抽取
      if (!productId) {
        productId = document.getElementById('CustomStickyTriggerBtn')?.getAttribute('data-product-id') || '';
        productTitle = document.querySelector('.productView-title')?.textContent.trim() || '';
      }

      const pageType = getPageType();
      const pageUrl = window.location.href;

      // 缓存状态至 window 以便关闭时计算时长
      window.sizeGuideTrackerState = {
        openTime: Date.now(),
        productId: productId,
        productTitle: productTitle,
        sourcePosition: sourcePosition
      };

      // 发送 click_size_guide 埋点事件
      if (typeof gtag === 'function') {
        gtag('event', 'click_size_guide', {
          product_id: productId.toString(),
          product_title: productTitle,
          source_position: sourcePosition,
          page_type: pageType,
          page_url: pageUrl
        });
      }
      console.log('📊 GA4 Event Tracked: click_size_guide', { productId, productTitle, sourcePosition, pageType, pageUrl });

      // 延迟检查弹窗状态以发送 view_size_guide 埋点事件
      setTimeout(() => {
        const sizeModal = document.getElementById('cider-size-modal-global');
        if (sizeModal && sizeModal.classList.contains('is-active')) {
          if (typeof gtag === 'function') {
            gtag('event', 'view_size_guide', {
              product_id: productId.toString(),
              product_title: productTitle,
              source_position: sourcePosition,
              page_type: pageType,
              page_url: pageUrl
            });
          }
          console.log('📊 GA4 Event Tracked: view_size_guide', { productId, productTitle, sourcePosition, pageType, pageUrl });
        }
      }, 150);
    }

    // ==================== 2. 监测关闭事件（时长统计） ====================
    const closeBtn = e.target.closest('[data-modal-close]');
    if (closeBtn && closeBtn.closest('#cider-size-modal-global')) {
      const sizeModal = document.getElementById('cider-size-modal-global');
      // 如果当前弹窗是打开状态（在被真正关闭前统计）
      if (sizeModal && sizeModal.classList.contains('is-active')) {
        let duration = 0;
        const state = window.sizeGuideTrackerState;
        if (state.openTime) {
          duration = (Date.now() - state.openTime) / 1000;
          state.openTime = null; // 重置防止多次触发
        }

        const pageType = getPageType();
        const pageUrl = window.location.href;
        const finalDuration = parseFloat(duration.toFixed(2));

        // 发送 close_size_guide 埋点事件
        if (typeof gtag === 'function') {
          gtag('event', 'close_size_guide', {
            product_id: state.productId.toString(),
            product_title: state.productTitle,
            source_position: state.sourcePosition || 'unknown',
            duration: finalDuration,
            page_type: pageType,
            page_url: pageUrl
          });
        }
        console.log('📊 GA4 Event Tracked: close_size_guide', { productId: state.productId, productTitle: state.productTitle, sourcePosition: state.sourcePosition, duration: finalDuration, pageType, pageUrl });
      }
    }

    // ==================== 3. 监测单位切换事件 ====================
    const toggle = e.target.closest('.sg26-toggle-wrap');
    if (toggle) {
      setTimeout(() => {
        const activeLabel = toggle.querySelector('.sg26-unit-label.active');
        const unit = activeLabel ? activeLabel.getAttribute('data-u') : 'cm';

        let productId = window.sizeGuideTrackerState.productId;
        let productTitle = window.sizeGuideTrackerState.productTitle;

        if (!productId) {
          const globalQShop = document.getElementById('GlobalQuickShop');
          if (globalQShop && globalQShop.classList.contains('is-open') && globalQShop.productData) {
            productId = globalQShop.productData.id || '';
            productTitle = globalQShop.productData.title || '';
          } else {
            productId = document.getElementById('CustomStickyTriggerBtn')?.getAttribute('data-product-id') || '';
            productTitle = document.querySelector('.productView-title')?.textContent.trim() || '';
          }
        }

        // 发送 switch_size_guide_unit 埋点事件
        if (typeof gtag === 'function') {
          gtag('event', 'switch_size_guide_unit', {
            product_id: productId.toString(),
            product_title: productTitle,
            unit: unit
          });
        }
        console.log('📊 GA4 Event Tracked: switch_size_guide_unit', { productId, productTitle, unit });
      }, 100);
    }
  });
})();
